/**
 * Step1 subpage that is used for `components/subpages/migration.jsx` when a visitors is at the `step1` phase of the migration process.
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/migration/step1.jsx
 * @class MigrationStep1
 * @since 0.0.1
 */

import React, {Component} from 'react';
import {validateMnemonic} from 'bip39';
import PropTypes from 'prop-types';
import {Radio} from '@rmwc/radio';
import {Switch} from '@rmwc/switch';
import {Button} from '@rmwc/button';
import {TextField} from '@rmwc/textfield';
import {LinearProgress} from '@rmwc/linear-progress';
import SearchSelect from '../../storybook/search-select';
import {Confirm, Eye} from '../../SvgIcons';

import {convertToArray} from '../../../lib/helpers/file-to-text';
import {parseGen2Dump, gen2PublicKeyToAddress} from '../../../lib/helpers/key-parser';
import bnFormat from '../../../lib/helpers/bn-format';
import {getKeysFromMnemonic} from '../../../lib/helpers/mnemonic-parser';
import translations from '../../../lib/translations';
import demoState from '../../../globals/demo-usage';

const REGEXP_BIP44 = /^M\/(\d+)H\/(\d+)H\/\d+H$/,
    MAX_KEYPOOL_SIZE = 512000;

class Steps1 extends Component {
    constructor(props) {
        super(props);
        this.myRef = React.createRef();
        const instance = this;
        let newState;

        instance.translate = translations.init(props);
        instance.getFiles = instance.getFiles.bind(instance);
        instance.selectedFileChanged = instance.selectedFileChanged.bind(instance);
        instance.mayProceed = instance.mayProceed.bind(instance);
        instance.handleMnemonicWords = instance.handleMnemonicWords.bind(instance);
        instance.handleGen2Choice = instance.handleGen2Choice.bind(instance);
        instance.handleAccount = instance.handleAccount.bind(instance);
        instance.handleExtraword = instance.handleExtraword.bind(instance);
        instance.handleMnemonicExtraPhrase = instance.handleMnemonicExtraPhrase.bind(
            instance
        );
        instance.propagateMnemonic = instance.propagateMnemonic.bind(instance);
        instance.handleExtraKeyVisibilty = instance.handleExtraKeyVisibilty.bind(
            instance
        );
        instance.handleAdvancedSettingsToggle = instance.handleAdvancedSettingsToggle.bind(
            instance
        );
        instance.handleKeyPoolSize = instance.handleKeyPoolSize.bind(instance);
        instance.handleDerivationPathSelection = instance.handleDerivationPathSelection.bind(
            instance
        );
        instance.handleCustomDeriavationPathInput = instance.handleCustomDeriavationPathInput.bind(
            instance
        );

        newState = {
            extraPhrase: false,
            account: '',
            mnemonicExtraPhrase: '',
            hidden: true,
            advancedSetting: false,
            derivationPath: 'M/44H/204H/0H',
            derivationSelection: 'coinomi',
            keyPoolSize: 1000,
            error: false,
            mnemonicWords: 24,
            filename: null,
            previewAddresses: [instance.translate.t('ADDRESSES_AFTER_MNEMONIC_PHRASE', true), '', '', '', ''],
            focusedMnemonicInput: null,
            isDemo: false
        };
        this.state = newState;
    }

    componentDidMount() {
        const data = demoState.$demo;
        data.watch(state => this.changeDemo(state));
    }

    componentDidUpdate(newProps) {
        if (this.props !== newProps) {
            let state = this.state,
                mnemonic, list, i, newState = {};
            if (state.isDemo) {
                mnemonic =
            'bless pet jar flavor fluid fall pledge tip weather panel parrot require orbit puzzle teach witness parent cool february quote dash mixture rotate over';
                list = mnemonic.split(' ');
                for (i = 1; i < 25; i++) {
                    newState['mnemonic' + i] = list[i - 1];
                    this.setState(newState);
                }
            }
        }
    }

    changeDemo(value) {
        this.setState({isDemo: value});
    }

    /**
   * Retrieves the Blob list out of the file input element. This is basically an Array with all selected files.
   *
   * @method getFiles
   * @return {Array} list with File Blobs
   * @since 0.0.1
   */
    getFiles() {
        const fileselector = this._inputNode;
        return fileselector ? fileselector.files : [];
    // return this._inputNode ? (this._inputNode.files || [FileLikeObject.createFile(this._inputNode)]) : [];
    }

    /**
   * Populate Mnemonic Phrase fields when a user pastes from the clipboard.
   * Will spread the entry, bases on spaces between the words, starting at the field that is pasted in.
   *
   * @method populateCopyPaste
   * @param field {Number} the field (index) where the paste happened
   * @param event {Object} event object with all information about the event, like the data
   * @since 0.0.1
   */
    populateCopyPaste(field, event) {
        event.preventDefault();
        const mnemonicWords = this.state.mnemonicWords,
            clipboardData = event.clipboardData || window.clipboardData,
            mnemonicPhrase = clipboardData.getData('Text');
        let extractWords = mnemonicPhrase.match(/\S+/g);
        if (!extractWords) {
            return;
        }
        let newState = {};
        extractWords.slice(0, mnemonicWords - field + 1).forEach((word, index) => {
            newState['mnemonic' + (index + field)] = word.toLowerCase();
        });

        this.setState(newState, this.propagateMnemonic);
    }

    generateWalletInfo() {
        const instance = this,
            state = instance.state,
            props = instance.props,
            translate = instance.translate,
            walletType = props.walletType,
            mnemonicWords = state.mnemonicWords;
        let inputHolder, i, extraPhrase, advancedButton, advancedSection, account,
            addressPreview, mnemonic, validMnemonic;

        if (walletType === 'wallet_dump') {
            return (
                <div className="image-upload" hidden={state.hideWalletDump}>
                    <form className="form">
                        <div
                            className="file-upload-wrapper"
                            data-text={state.filename || translate.t('NO_FILE_SELECTED', true)}
                            data-upload-file={translate.t('UPLOAD_FILE')} >
                            <input
                                className="file-upload-field"
                                name="file-upload-field"
                                onChange={instance.selectedFileChanged}
                                ref={node => (instance._inputNode = node)}
                                type="file"/>
                        </div>
                    </form>
                </div>
            );
        }
        else if (walletType === 'gen_2_key') {
            return (
                <div className="input-wrapper" hidden={state.hidegen2Key}>
                    <TextField
                        className="input"
                        label={translate.t('ENTER_GEN2_PRIV_KEY', true)}
                        onChange={props.handlePrivate}
                        outlined
                        value={props.priv}/>
                </div>
            );
        }

        // else = mnemonic phrase

        inputHolder = [];

        for (i = 1; i <= state.mnemonicWords; i++) {
            const index = i;
            inputHolder.push(
                <div className={this.state.focusedMnemonicInput === index ? 'focused' : ''} key={i}>
                    {i}.
                    <input
                        onBlur={() => this.setState({focusedMnemonicInput: null})}
                        onChange={instance.handleMnemonicKey.bind(this, i)}
                        onFocus={() => this.setState({focusedMnemonicInput: index})}
                        onPaste={this.populateCopyPaste.bind(this, i)}
                        value={state['mnemonic' + i] || ''}/>
                </div>
            );
        }

        if (state.extraPhrase) {
            const typer = state.hidden ? 'password' : 'text';
            extraPhrase = (
                <div className="extra">
                    <input
                        onChange={instance.handleMnemonicExtraPhrase}
                        placeholder={translate.t('ENTER_PHRASE', true)}
                        type={typer}
                        value={state.mnemonicExtraPhrase || ''}/>
                    <div onClick={instance.handleExtraKeyVisibilty}>
                        <Eye onClick={instance.handleExtraKeyVisibilty} />
                    </div>
                </div>
            );
        }

        if (state.derivationSelection === 'coinomi') {
            account = (
                <input
                    onChange={instance.handleAccount}
                    placeholder={translate.t('OPTIONAL_WALLET_NR', true)}
                    value={state.account} />
            );
        }

        if (state.advancedSetting) {
            advancedButton = (
                <button onClick={instance.handleAdvancedSettingsToggle}>
          &#45; {translate.t('HIDE_ADVANCED_SETTINGS', true)}
                </button>
            );

            const selectOpts = [
                {
                    label: 'Coinomi',
                    value: 'coinomi',
                },
                {
                    label: `Energi ${translate.t('GEN1', true)}`,
                    value: 'gen1',
                },
                {
                    label: `Energi ${translate.t('GEN2', true)}`,
                    value: 'gen2',
                },
                {
                    label: translate.t('CUSTOM', true),
                    value: 'custom',
                },
            ];

            advancedSection = (
                <div className="advanced-section">
                    <p>{translate.t('DERIVATION_PATH', true)} (BIP44)</p>
                    <div className="custom-select">
                        <SearchSelect
                            onChange={instance.handleDerivationPathSelection}
                            options={selectOpts}
                            searchBox={false}
                            value={state.derivationSelection} />
                    </div>
                    <input
                        disabled={state.derivationSelection !== 'custom'}
                        onChange={instance.handleCustomDeriavationPathInput}
                        value={state.derivationPath} />
                    <p>{translate.t('KEY_POOL_SIZE', true)}</p>
                    <input
                        onChange={instance.handleKeyPoolSize}
                        placeholder="1000"
                        value={state.keyPoolSize} />
                </div>
            );
        }
        else {
            advancedButton = (
                <button onClick={instance.handleAdvancedSettingsToggle}>
          &#43; {translate.t('SHOW_ADVANCED_SETTINGS', true)}
                </button>
            );
        }

        mnemonic = instance.getMnemonic();
        validMnemonic = validateMnemonic(mnemonic);

        addressPreview = (
            <ul className="address-preview">
                {state.previewAddresses.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
        );

        return (
            <div className="mnemonic">
                <p>{translate.t('ENTER_MNEM_PHRASE', true)}</p>

                <div className="radios">
                    <Radio
                        checked={mnemonicWords === 12}
                        className="radio"
                        onChange={() => instance.handleMnemonicWords(12)}
                        value="12">
                        {' 12 '}<span className="desc">- {translate.t('WORDS')}</span>
                    </Radio>
                    <Radio
                        checked={mnemonicWords === 18}
                        className="radio"
                        onChange={() => instance.handleMnemonicWords(18)}
                        value="18">
                        {' 18 '}<span className="desc">- {translate.t('WORDS')}</span>
                    </Radio>
                    <Radio
                        checked={mnemonicWords === 24}
                        className="radio"
                        onChange={() => instance.handleMnemonicWords(24)}
                        value="24">
                        {' 24 '}<span className="desc">- {translate.t('WORDS')}</span>
                    </Radio>
                </div>

                <div className="inputs">{inputHolder}</div>

                <div className="extra-word">
                    <h4>{translate.t('HAVE_A_PHRASE', true)}</h4>
                    <Switch
                        checked={state.extraPhrase}
                        className="switch"
                        onChange={instance.handleExtraword}/>
                </div>

                {extraPhrase}

                <div className="settings-section">
                    {account}
                </div>

                <div className="advanced-settings">
                    {advancedButton}
                    {advancedSection}
                </div>

                <div className="address-interaction">
                    <p>{translate.t('FIRST_INTERACTION_ADDRESS', true)}</p>
                    {addressPreview}
                </div>

                <div className="mnemonic-footer">
                    <Button
                        disabled={state.error || !validMnemonic}
                        label={translate.t('USE_MNEM_SETTINGS', true)}
                        onClick={instance.propagateMnemonic.bind(instance, true)}
                        unelevated />
                </div>
            </div>
        );
    }

    handleMnemonicWords(mnemonicWords) {
        let newState = {mnemonicWords},
            i;
        if (mnemonicWords!==24) {
            for (i = 19; i <= 24; i++) {
                newState['mnemonic' + i] = '';
            }
        }
        if (mnemonicWords===12) {
            for (i = 13; i <= 18; i++) {
                newState['mnemonic' + i] = '';
            }
        }
        this.setState(newState, this.propagateMnemonic);
    }

    /**
   * Checkes whether the selected Gen2 accounts have balance and the user is allowed to proceed to step2.
   *
   * @method mayProceed
   * @return {Boolean} whether proceeding to step2 is allowed
   * @since 0.0.1
   */
    mayProceed() {
        return this.props.hasBalance;
    }

    /**
   * Callback for the File Selector `onChange`, is used to examine the selected file.
   *
   * @method selectedFileChanged
   * @since 0.0.1
   */
    selectedFileChanged() {
        let dumpAsLines, privateKeys;
        const instance = this,
            files = instance.getFiles(),
            file = files[0]; // file === blob
        if (!(file instanceof window.Blob)) {
            // only show error is file is not undefinde,
            // because 'undefined' means the user pressed cancel
            file && console.warn('No proper fileobject');
            instance.setState({filename: null});
        }
        else {
            try {
                instance.setState({filename: file.name}, async () => {
                    dumpAsLines = await convertToArray(file);
                    privateKeys = parseGen2Dump(dumpAsLines);
                    instance.props.handleDump(privateKeys);
                });
            }
            catch (err) {
                instance.setState({filename: null});
                console.error(err);
            }
        }
    }

    handleAccount(e) {
        const value = e.target.value,
            account = (value==='') ? value : parseInt(value, 10);
        this.setState({
            account,
            derivationPath: 'M/44H/204H/'+this.getAccount(account)+'H',
        }, () => this.propagateMnemonic());
    }

    /**
   * Method for switching between wallet types
   * @method handleGen2Choice
   * @param {*} value
   * @since 0.0.2
   */
    handleGen2Choice(walletType) {
        this.setState({filename: null}, () => {
            this.props.onChangeGen2Choice(walletType);
        });
    }

    /**
   * Method for marking a step as complete
   * @method handleStepCompletion
   * @param {*} value
   * @since 0.0.2
   */
    handleStepCompletion(value) {
        this.props.handleChangeStep(value);
        this.props.completeStep1(true);
        window.scrollTo(0, this.myRef.current.offsetTop - 80);
    }

    getAccount(input) {
        const account = input || this.state.account || 1; // zero or empty string should return the first account
        return account - 1;
    }

    getMnemonic() {
        const state = this.state;
        let list = [],
            i;

        for (i = 1; i <= state.mnemonicWords; i++) {
            list.push(state['mnemonic' + i] || '');
        }
        return list.join(' ');
    }

    getMnemonicData() {
        const instance = this,
            mnemonic = instance.getMnemonic(),
            state = instance.state,
            match = state.derivationPath.match(REGEXP_BIP44);

        return {
            mnemonic,
            extraPhrase: state.extraPhrase ? state.mnemonicExtraPhrase : '',
            purpose: match[1] ? parseInt(match[1], 10) : 44,
            coinType: match[2] ? parseInt(match[2], 10) : 204,
            account: instance.getAccount(),
            keyPoolSize: state.keyPoolSize || 1000,
            valid: validateMnemonic(mnemonic)
        };
    }

    handleMnemonicKey(index, e) {
        let newState = {};
        newState['mnemonic' + index] = e.target.value.trim().toLowerCase();
        this.setState(newState, this.propagateMnemonic);
    }

    handleMnemonicExtraPhrase(e) {
        this.setState(
            {
                mnemonicExtraPhrase: e.target.value,
            },
            this.propagateMnemonic
        );
    }

    async propagateMnemonic(askBalance) {
        let previewAddresses;
        const instance = this,
            getMnemonicData = instance.getMnemonicData(),
            mnemonic = getMnemonicData.mnemonic,
            extraPhrase = getMnemonicData.extraPhrase,
            purpose = getMnemonicData.purpose,
            coinType = getMnemonicData.coinType,
            account = getMnemonicData.account;

        if (typeof askBalance!=='boolean') {
            askBalance = false;
        }

        instance.props.onChangeMnemonic(
            mnemonic,
            extraPhrase,
            purpose,
            coinType,
            account,
            getMnemonicData.keyPoolSize,
            askBalance
        );

        // also try to search for the first Addresses that belong to the mneominc phrase:
        if (validateMnemonic(mnemonic)) {
            previewAddresses = await getKeysFromMnemonic(mnemonic, extraPhrase, purpose, coinType, account, true, 0, 5);
            previewAddresses = previewAddresses.map(a => gen2PublicKeyToAddress(a.publicKey));
        }
        else {
            previewAddresses = [
                instance.translate.t('ADDRESSES_AFTER_MNEMONIC_PHRASE', true),
                '',
                '',
                '',
                '',
            ];
        }
        instance.setState({previewAddresses});
    }

    handleExtraword() {
        this.setState({
            extraPhrase: !this.state.extraPhrase,
            mnemonicExtraPhrase: null,
        }, this.propagateMnemonic);
    }

    handleExtraKeyVisibilty() {
        this.setState({hidden: !this.state.hidden});
    }

    handleAdvancedSettingsToggle() {
        this.setState({advancedSetting: !this.state.advancedSetting});
    }

    handleDerivationPathSelection(value) {
        const instance = this;
        let newState;

        switch (value) {
        case 'gen1':
            newState = {
                derivationPath: 'M/44H/5H/0H',
                derivationSelection: 'gen1',
            };
            break;
        case 'gen2':
            newState = {
                derivationPath: 'M/44H/9797H/0H',
                derivationSelection: 'gen2',
            };
            break;
        case 'coinomi':
            newState = {
                derivationPath: 'M/44H/204H/'+instance.getAccount()+'H',
                derivationSelection: 'coinomi',
            };
            break;
        case 'custom':
            newState = {derivationSelection: 'custom'};
        }
        instance.setState(newState, () => instance.propagateMnemonic());
    }

    handleCustomDeriavationPathInput(e) {
        const derivationPath = e.target.value.toUpperCase();

        this.setState({
            derivationPath,
            error: !REGEXP_BIP44.test(derivationPath)
        }, () => this.propagateMnemonic());
    }

    handleKeyPoolSize(e) {
        const value = e.target.value;
        let keyPoolSize = (value === '') ? value : parseInt(value, 10);
        if (keyPoolSize > MAX_KEYPOOL_SIZE) {
            keyPoolSize = MAX_KEYPOOL_SIZE;
        }
        this.setState({keyPoolSize}, () => this.propagateMnemonic());
    }

    /**
   * React render-method --> renders the Component.
   *
   * @method render
   * @return {ReactComponent}
   * @since 0.0.1
   */
    render() {
        const instance = this,
            translate = instance.translate,
            walletInfo = instance.generateWalletInfo(),
            props = instance.props,
            locales = props.langInfo && props.langInfo.locales,
            balanceLoading = props.loading,
            walletType = props.walletType;
        let balance, loading, confirm, detailsStep1;

        if (props.step === 1) {
            if (balanceLoading) {
                loading = (
                    <>
                        <span>{translate.t('RETRIEVING_BALANCE', true)}</span>
                        <LinearProgress className="loader" />
                    </>
                );
            }
            if (props.balance !== '') {
                balance = (
                    <span className="balance">
                        {translate.t('BALANCE_TOBE_TRANS', true)}: <b>{bnFormat(locales, props.balance)}</b>&nbsp; NRG
                    </span>
                );
            }
            detailsStep1 = (
                <div className="details">
                    <p>{translate.t('SELECT_GEN2_WALLET', true)}:</p>
                    <div className="radios">
                        <Radio
                            checked={walletType === 'wallet_dump'}
                            className="radio"
                            disabled={balanceLoading}
                            onChange={() => instance.handleGen2Choice('wallet_dump')}
                            value="wallet_dump">
                            {translate.t('GEN2_WALLET_DUMP', true)}
                        </Radio>
                        <Radio
                            checked={walletType === 'gen_2_key'}
                            className="radio"
                            disabled={balanceLoading}
                            onChange={() => instance.handleGen2Choice('gen_2_key')}
                            value="gen_2_key">
                            {translate.t('SINGLE_GEN2_KEY', true)}
                        </Radio>
                        <Radio
                            checked={walletType === 'mnemonic'}
                            className="radio"
                            disabled={balanceLoading}
                            onChange={() => instance.handleGen2Choice('mnemonic')}
                            value="mnemonic">
                            {translate.t('MNEM_PHRASE', true)}
                        </Radio>
                    </div>

                    {walletInfo}

                    {loading}

                    {balance}

                    <div className="buttons">
                        <Button
                            className="button-step1"
                            disabled={!instance.mayProceed() || balanceLoading}
                            label={translate.t('CONTINUE', true)}
                            onClick={() => instance.handleStepCompletion(2)}
                            unelevated/>
                    </div>
                </div>
            );
        }
        if (props.step1 && props.hasBalance) {
            confirm = <Confirm />;
        }

        const isActive = props.step === 1 ? 'active' : null;
        const isApproved = (props.step1 && props.hasBalance) ? 'approved' : null;
        return (
            <div className="step1" ref={this.myRef}>
                <div className="step-head">
                    <h3 className={`${isActive} ${isApproved}`}>{translate.t('STEP1')}.</h3>
                    {confirm}
                </div>
                {detailsStep1}
            </div>
        );
    }
}

export default Steps1;

Steps1.defaultProps = {
    loading: false,
};

Steps1.propTypes = {
    balance: PropTypes.string.isRequired,
    completeStep1: PropTypes.func.isRequired,
    handleChangeStep: PropTypes.func.isRequired,
    handleDump: PropTypes.func.isRequired,
    handlePrivate: PropTypes.func.isRequired,
    hasBalance: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    onChangeGen2Choice: PropTypes.func.isRequired,
    onChangeMnemonic: PropTypes.func.isRequired,
    priv: PropTypes.string.isRequired,
    step: PropTypes.number.isRequired,
    step1: PropTypes.bool.isRequired,
};
